import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

export const PageSection = styled.div`
  display: block;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

export const PageTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.8rem;
  font-weight: bold;
`;
export const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #ff6b35;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #333;
    border-top: 3px solid #ff6b35;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  p {
    font-size: 1.1rem;
    margin: 0;
  }
`;

export const Button = styled.button`
  padding: 0.8rem 1.2rem;
  min-height: 3rem;
  border: none;
  border-radius: 8px;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: bold;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) => {
    switch (props.$variant) {
      case "primary":
        return "#ff6b35";
      case "secondary":
        return "#666";
      case "danger":
        return "#e74c3c";
      case "success":
        return "#27ae60";
      default:
        return "#ff6b35";
    }
  }};
  color: #ffffff;

  &:hover {
    background: ${(props) => {
      switch (props.$variant) {
        case "primary":
          return "#e55a2b";
        case "secondary":
          return "#555";
        case "danger":
          return "#c0392b";
        case "success":
          return "#229954";
        default:
          return "#e55a2b";
      }
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  transition: transform 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;

  break-inside: avoid;
  width: 100%;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(255, 107, 53, 0.2);
  }
`;

export const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #ff6b35;
  margin-bottom: 1rem;
  display: flex;
  animation: ${float} 3s ease-in-out infinite;
  align-items: center;
`;

export const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 2200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 1300px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 950px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    grid-template-columns: 1fr;
  }
`;

export const CategoryCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 16px;
  padding: 1.8rem;
  border: 2px solid
    ${(props) =>
      props.$active ? "rgba(39, 174, 96, 0.3)" : "rgba(231, 76, 60, 0.3)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  break-inside: avoid;
  width: 100%;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${(props) =>
      props.$active
        ? "linear-gradient(90deg, #27ae60, #2ecc71)"
        : "linear-gradient(90deg, #e74c3c, #c0392b)"};
    border-radius: 16px 16px 0 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: ${(props) =>
      props.$active
        ? "radial-gradient(circle, rgba(39, 174, 96, 0.1) 0%, transparent 70%)"
        : "radial-gradient(circle, rgba(231, 76, 60, 0.1) 0%, transparent 70%)"};
    pointer-events: none;
    z-index: 0;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: ${(props) =>
      props.$active ? "rgba(39, 174, 96, 0.6)" : "rgba(231, 76, 60, 0.6)"};
    box-shadow: ${(props) =>
      props.$active
        ? "0px 20px 40px rgba(39, 174, 96, 0.25)"
        : "0px 20px 40px rgba(231, 76, 60, 0.25)"};
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const NewsCard = styled(CategoryCard)``;

export const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

export const CategoryTitle = styled.h4`
  color: #fff;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
`;

export const CategoryActionsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const CategoryDescription = styled.p`
  color: #b8b8b8;
  margin: 1rem 0 1.5rem 0;
  line-height: 1.6;
  font-size: 0.95rem;
  opacity: 0.9;
  max-height: 4.8rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const CategoryFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 1.5rem;
`;

export const CategoryFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${(props) => (props.$active ? "#27ae60" : "#666")};
  font-weight: 500;
  transition: color 0.3s ease;
`;

export const ProductsCount = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  color: #ff6b35;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.4rem 0.8rem;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 107, 53, 0.2);
`;

export const StatusBadge = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;

  color: ${(props) => {
    switch (props.$status) {
      case "completed":
      case "active":
        return "#27ae60";
      case "processing":
        return "#f39c12";
      case "inactive":
        return "#e74c3c";
      default:
        return "#cccccc";
    }
  }};
  background: ${(props) => {
    switch (props.$status) {
      case "completed":
      case "active":
        return "rgba(39, 174, 96, 0.2)";
      case "processing":
        return "rgba(243, 156, 18, 0.2)";
      case "inactive":
        return "rgba(231, 76, 60, 0.2)";
      default:
        return "rgba(204, 204, 204, 0.2)";
    }
  }};
`;

export const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

export const Modal = styled.div`
  display: ${(props) => (props.$active ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
  backdrop-filter: blur(5px);
  align-items: center;
  justify-content: center;
`;

export const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid #ff6b35;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const ModalTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.5rem;
  font-weight: bold;
`;

export const ModalClose = styled.button`
  background: none;
  border: none;
  color: #ff6b35;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: rotate(90deg);
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const FormLabel = styled.label`
  display: block;
  color: #ff6b35;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  transition: border-color 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }

  &::placeholder {
    color: #888;
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  transition: border-color 0.3s ease;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }

  &::placeholder {
    color: #888;
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const CheckboxLabel = styled.label`
  color: #ff6b35;
  cursor: pointer;
  user-select: none;
`;

export const FormActionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

// Modal styles
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

export const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #fff;
  border-radius: 50%;
  font-size: 1rem;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25);
  -webkit-backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ff6b35;
  }
`;

export const ConfirmBox = styled.div`
  background: #1a1a1a;
  padding: 3rem 2rem;
  border-radius: 1.6rem;
  max-width: 400px;
  width: 100%;
  color: white;
  position: relative;
  text-align: center;

  h3 {
    font-family: "Poppins-SemiBold";
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #ff6b35;
  }

  p {
    font-family: "Poppins-Medium";
    font-size: 1rem;
    margin-bottom: 2rem;
    color: #ccc;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

export const Cancel = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.8rem;
  background: #333;
  color: white;
  border: none;
  font-family: "Poppins-SemiBold";
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background: #444;
  }
`;

export const Confirm = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.8rem;
  background: #ff6b35;
  color: white;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
  font-size: 1rem;
  font-family: "Poppins-SemiBold";
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};

  &:hover {
    background: #ff6b35;
  }
`;
